import React, { useState, useEffect } from 'react';
import { adminInvoicesAPI, adminQuotesAPI, adminProductsAPI, paymentAPI } from '../../services/api';

export default function AdminInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null); // Pour édition
  
  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Formulaire de paiement
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    method: 'manual',
    reference: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  // Formulaire de création
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    items: [{ product_id: '', description: '', quantity: 1, unit_price: 0, total_ht: 0 }],
    tva_rate: 20.0,
    discount_type: '',
    discount_value: 0,
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    paid_date: '',
    notes: '',
    quote_id: null
  });
  
  // Formulaire depuis devis
  const [quoteFormData, setQuoteFormData] = useState({
    quote_id: '',
    amount: 0,
    tva_rate: 20.0
  });

  useEffect(() => {
    loadInvoices();
    loadQuotes();
    loadProducts();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminInvoicesAPI.list();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement factures:', err);
      setError('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const loadQuotes = async () => {
    try {
      const data = await adminQuotesAPI.list();
      setQuotes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement devis:', err);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await adminProductsAPI.getAll();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement produits:', err);
    }
  };

  const handleProductSelect = (index, productId) => {
    const newItems = [...formData.items];
    
    if (productId === 'manual') {
      // Mode saisie manuelle
      newItems[index] = {
        product_id: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        total_ht: 0
      };
    } else if (productId) {
      // Produit sélectionné
      const product = products.find(p => p.id === productId);
      if (product) {
        const price = product.price === 'Sur devis' ? 0 : parseFloat(product.price) || 0;
        newItems[index] = {
          product_id: productId,
          description: product.name,
          quantity: 1,
          unit_price: price,
          total_ht: price
        };
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total_ht = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', description: '', quantity: 1, unit_price: 0, total_ht: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      // Filtrer les champs non nécessaires pour le backend
      const invoiceData = {
        ...formData,
        // Convertir discount_type vide en null
        discount_type: formData.discount_type || null,
        // Convertir les dates vides en null
        issue_date: formData.issue_date || null,
        due_date: formData.due_date || null,
        paid_date: formData.paid_date || null,
        // Convertir les chaînes vides en null
        client_phone: formData.client_phone || null,
        client_address: formData.client_address || null,
        notes: formData.notes || null,
        quote_id: formData.quote_id || null,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          total_ht: parseFloat(item.total_ht) || 0
        }))
      };
      
      if (editingInvoice) {
        // Mode édition
        await adminInvoicesAPI.update(editingInvoice.id, invoiceData);
        alert('✅ Facture modifiée avec succès!');
      } else {
        // Mode création
        await adminInvoicesAPI.create(invoiceData);
        alert('✅ Facture créée avec succès!');
      }
      
      setShowCreateModal(false);
      setEditingInvoice(null);
      loadInvoices();
      resetForm();
    } catch (err) {
      console.error('Erreur facture:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      alert('❌ Erreur:\n\n' + errorMessage);
    }
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      client_name: invoice.client_name || '',
      client_email: invoice.client_email || '',
      client_phone: invoice.client_phone || '',
      client_address: invoice.client_address || '',
      items: invoice.items && invoice.items.length > 0 ? invoice.items : [{ product_id: '', description: '', quantity: 1, unit_price: 0, total_ht: 0 }],
      tva_rate: invoice.tva_rate || 20.0,
      discount_type: invoice.discount_type || '',
      discount_value: invoice.discount_value || 0,
      issue_date: invoice.issue_date ? new Date(invoice.issue_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '',
      paid_date: invoice.paid_date ? new Date(invoice.paid_date).toISOString().split('T')[0] : '',
      notes: invoice.notes || '',
      quote_id: invoice.quote_id || null
    });
    setShowCreateModal(true);
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler cette facture ?')) {
      try {
        await adminInvoicesAPI.update(invoiceId, { status: 'cancelled' });
        loadInvoices();
        alert('✅ Facture annulée');
      } catch (err) {
        console.error('Erreur annulation:', err);
        alert('Erreur lors de l\'annulation de la facture');
      }
    }
  };

  const handleCreateFromQuote = async (e) => {
    e.preventDefault();
    try {
      await adminInvoicesAPI.createFromQuote(
        quoteFormData.quote_id,
        quoteFormData.amount,
        quoteFormData.tva_rate
      );
      setShowQuoteModal(false);
      loadInvoices();
      setQuoteFormData({ quote_id: '', amount: 0, tva_rate: 20.0 });
    } catch (err) {
      console.error('Erreur création facture depuis devis:', err);
      alert('Erreur lors de la création de la facture depuis le devis');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    if (window.confirm('Marquer cette facture comme payée ?')) {
      try {
        await adminInvoicesAPI.markPaid(invoiceId, 'manual', `MANUAL-${Date.now()}`);
        loadInvoices();
      } catch (err) {
        console.error('Erreur marquage payé:', err);
        alert('Erreur lors du marquage de la facture comme payée');
      }
    }
  };

  const handleDelete = async (invoiceId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) {
      try {
        await adminInvoicesAPI.delete(invoiceId);
        loadInvoices();
      } catch (err) {
        console.error('Erreur suppression:', err);
        alert('Erreur lors de la suppression de la facture');
      }
    }
  };

  const handleOpenPaymentModal = (invoice) => {
    setPaymentInvoice(invoice);
    const remainingAmount = invoice.amount_due || (invoice.total_ttc - (invoice.amount_paid || 0));
    setPaymentData({
      amount: remainingAmount,
      method: 'manual',
      reference: '',
      notes: '',
      date: new Date().toISOString().split('T')[0]
    });
    setShowPaymentModal(true);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!paymentInvoice) return;
    
    try {
      await adminInvoicesAPI.addPayment(paymentInvoice.id, {
        ...paymentData,
        amount: parseFloat(paymentData.amount)
      });
      setShowPaymentModal(false);
      setPaymentInvoice(null);
      loadInvoices();
      alert('Paiement ajouté avec succès!');
    } catch (err) {
      console.error('Erreur ajout paiement:', err);
      alert(err.response?.data?.detail || 'Erreur lors de l\'ajout du paiement');
    }
  };

  const handleGeneratePayPalLink = async (invoice) => {
    try {
      const remainingAmount = (invoice.amount_due || invoice.total_ttc || 0);
      
      if (remainingAmount <= 0) {
        alert('⚠️ Cette facture est déjà entièrement payée.');
        return;
      }

      const confirmed = window.confirm(`Générer un lien PayPal pour ${remainingAmount.toFixed(2)}€ (montant restant dû) ?`);
      if (!confirmed) return;

      const result = await paymentAPI.generateInvoicePaymentLink(
        invoice.id,
        remainingAmount,
        'EUR',
        `Facture ${invoice.invoice_number} - ${invoice.client_name}`
      );

      if (result.success && result.payment_link) {
        // Copier le lien dans le presse-papiers
        navigator.clipboard.writeText(result.payment_link).then(() => {
          alert(`✅ Lien PayPal généré et copié!\n\n${result.payment_link}\n\nVous pouvez maintenant l'envoyer au client par email.`);
        }).catch(() => {
          alert(`✅ Lien PayPal généré:\n\n${result.payment_link}\n\nCopiez ce lien et envoyez-le au client.`);
        });
        
        // Recharger la liste des factures
        loadInvoices();
      } else {
        throw new Error('Erreur lors de la génération du lien');
      }
    } catch (err) {
      console.error('Erreur génération lien PayPal:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      alert('❌ Erreur lors de la génération du lien PayPal:\n\n' + errorMessage);
    }
  };


  const resetForm = () => {
    setFormData({
      client_name: '',
      client_email: '',
      client_phone: '',
      client_address: '',
      items: [{ product_id: '', description: '', quantity: 1, unit_price: 0, total_ht: 0 }],
      tva_rate: 20.0,
      discount_type: '',
      discount_value: 0,
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      paid_date: '',
      notes: '',
      quote_id: null
    });
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = 
      inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.client_email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const formatPriceFCFA = (priceEUR) => {
    const FCFA_RATE = 655.957;
    const priceFCFA = priceEUR * FCFA_RATE;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(priceFCFA);
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: 'bg-green-100 text-green-800',
      partially_paid: 'bg-blue-100 text-blue-800',
      unpaid: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      paid: 'Payée',
      partially_paid: 'Partiellement payée',
      unpaid: 'Non payée',
      cancelled: 'Annulée'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-lg">Chargement des factures...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Gestion des Factures</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowQuoteModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Créer depuis Devis
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-yellow-600 text-black rounded hover:bg-yellow-700"
          >
            Nouvelle Facture
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">
          {error}
        </div>
      )}

      {/* Filtres */}
      <div className="mb-6 flex gap-4">
        <input
          type="text"
          placeholder="Rechercher par numéro, nom ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border rounded"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded"
        >
          <option value="all">Tous les statuts</option>
          <option value="unpaid">Non payées</option>
          <option value="paid">Payées</option>
          <option value="cancelled">Annulées</option>
        </select>
      </div>

      {/* Liste des factures */}
      {filteredInvoices.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded">
          <p className="text-gray-500">Aucune facture trouvée</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Numéro</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Échéance</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Montant TTC (€)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Montant TTC (FCFA)</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-sm">{invoice.invoice_number}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium">{invoice.client_name}</div>
                    <div className="text-xs text-gray-500">{invoice.client_email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{formatDate(invoice.issue_date)}</td>
                  <td className="px-4 py-3 text-sm">{formatDate(invoice.due_date)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-semibold">{formatPrice(invoice.total_ttc)}</div>
                    {invoice.amount_paid > 0 && invoice.status !== 'paid' && (
                      <div className="text-xs text-gray-500">
                        Payé: {formatPrice(invoice.amount_paid)}<br/>
                        Restant: {formatPrice(invoice.amount_due || (invoice.total_ttc - invoice.amount_paid))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-semibold">{formatPriceFCFA(invoice.total_ttc)}</div>
                    {invoice.amount_paid > 0 && invoice.status !== 'paid' && (
                      <div className="text-xs text-gray-500">
                        Payé: {formatPriceFCFA(invoice.amount_paid)}<br/>
                        Restant: {formatPriceFCFA(invoice.amount_due || (invoice.total_ttc - invoice.amount_paid))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(invoice.status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-1 flex-wrap">
                      {(invoice.status === 'unpaid' || invoice.status === 'partially_paid') && (
                        <button
                          onClick={() => handleOpenPaymentModal(invoice)}
                          className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                          title="Ajouter un paiement"
                        >
                          💰 Paiement
                        </button>
                      )}
                      {(invoice.status === 'unpaid' || invoice.status === 'partially_paid') && (
                        <button
                          onClick={() => handleGeneratePayPalLink(invoice)}
                          className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                          title="Générer un lien de paiement PayPal"
                        >
                          💳 Lien PayPal
                        </button>
                      )}
                      <button
                        onClick={() => adminInvoicesAPI.downloadPDF(invoice.id)}
                        className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                        title="Télécharger PDF"
                      >
                        📄 PDF
                      </button>
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        title="Afficher les détails"
                      >
                        👁️ Afficher
                      </button>
                      {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                        <button
                          onClick={() => handleEditInvoice(invoice)}
                          className="px-2 py-1 bg-yellow-600 text-black text-xs rounded hover:bg-yellow-700"
                          title="Modifier la facture"
                        >
                          ✏️ Modifier
                        </button>
                      )}
                      {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                        <button
                          onClick={() => handleCancelInvoice(invoice.id)}
                          className="px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700"
                          title="Annuler la facture"
                        >
                          🚫 Annuler
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(invoice.id)}
                        className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                        title="Supprimer définitivement"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Détails */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedInvoice(null)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Facture {selectedInvoice.invoice_number}</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Informations Client</h4>
                <p><strong>Nom:</strong> {selectedInvoice.client_name}</p>
                <p><strong>Email:</strong> {selectedInvoice.client_email}</p>
                <p><strong>Téléphone:</strong> {selectedInvoice.client_phone || '-'}</p>
                <p><strong>Adresse:</strong> {selectedInvoice.client_address || '-'}</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Dates</h4>
                <p><strong>Date d'émission:</strong> {formatDate(selectedInvoice.issue_date)}</p>
                <p><strong>Date d'échéance:</strong> {formatDate(selectedInvoice.due_date)}</p>
                {selectedInvoice.paid_date && <p><strong>Date de paiement:</strong> {formatDate(selectedInvoice.paid_date)}</p>}
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Articles</h4>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Description</th>
                      <th className="px-2 py-1 text-right">Qté</th>
                      <th className="px-2 py-1 text-right">Prix HT (€)</th>
                      <th className="px-2 py-1 text-right">Prix HT (FCFA)</th>
                      <th className="px-2 py-1 text-right">Total HT (€)</th>
                      <th className="px-2 py-1 text-right">Total HT (FCFA)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-2 py-1">{item.description}</td>
                        <td className="px-2 py-1 text-right">{item.quantity}</td>
                        <td className="px-2 py-1 text-right">{formatPrice(item.unit_price)}</td>
                        <td className="px-2 py-1 text-right">{formatPriceFCFA(item.unit_price)}</td>
                        <td className="px-2 py-1 text-right">{formatPrice(item.total_ht)}</td>
                        <td className="px-2 py-1 text-right">{formatPriceFCFA(item.total_ht)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="border-t pt-4">
                {selectedInvoice.discount_value > 0 && (
                  <>
                    <div className="flex justify-between mb-2 text-sm">
                      <span>Sous-total HT:</span>
                      <div className="text-right">
                        <div className="font-semibold">{formatPrice(selectedInvoice.subtotal_ht || selectedInvoice.total_ht)}</div>
                        <div className="text-xs text-gray-500">{formatPriceFCFA(selectedInvoice.subtotal_ht || selectedInvoice.total_ht)}</div>
                      </div>
                    </div>
                    <div className="flex justify-between mb-2 text-sm text-green-600">
                      <span>Remise ({selectedInvoice.discount_value}%):</span>
                      <div className="text-right">
                        <div className="font-semibold">- {formatPrice(selectedInvoice.discount_amount || 0)}</div>
                        <div className="text-xs">- {formatPriceFCFA(selectedInvoice.discount_amount || 0)}</div>
                      </div>
                    </div>
                  </>
                )}
                <div className="flex justify-between mb-2">
                  <span>Total HT:</span>
                  <div className="text-right">
                    <div className="font-semibold">{formatPrice(selectedInvoice.total_ht)}</div>
                    <div className="text-xs text-gray-500">{formatPriceFCFA(selectedInvoice.total_ht)}</div>
                  </div>
                </div>
                <div className="flex justify-between mb-2">
                  <span>TVA ({selectedInvoice.tva_rate}%):</span>
                  <div className="text-right">
                    <div className="font-semibold">{formatPrice(selectedInvoice.tva_amount)}</div>
                    <div className="text-xs text-gray-500">{formatPriceFCFA(selectedInvoice.tva_amount)}</div>
                  </div>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total TTC:</span>
                  <div className="text-right">
                    <div>{formatPrice(selectedInvoice.total_ttc)}</div>
                    <div className="text-sm text-gray-600">{formatPriceFCFA(selectedInvoice.total_ttc)}</div>
                  </div>
                </div>
              </div>
              
              {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Historique des Paiements</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left">Date</th>
                        <th className="px-2 py-1 text-left">Méthode</th>
                        <th className="px-2 py-1 text-right">Montant</th>
                        <th className="px-2 py-1 text-left">Référence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.payments.map((payment, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-2 py-1">{formatDate(payment.date)}</td>
                          <td className="px-2 py-1">{payment.method}</td>
                          <td className="px-2 py-1 text-right font-semibold">{formatPrice(payment.amount)}</td>
                          <td className="px-2 py-1 text-xs text-gray-600">{payment.reference || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 p-3 bg-blue-50 rounded">
                    <div className="flex justify-between mb-1">
                      <span className="font-semibold">Total payé:</span>
                      <span className="font-semibold text-green-600">{formatPrice(selectedInvoice.amount_paid || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Restant dû:</span>
                      <span className="font-semibold text-orange-600">{formatPrice(selectedInvoice.amount_due || (selectedInvoice.total_ttc - (selectedInvoice.amount_paid || 0)))}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedInvoice.notes && (
                <div>
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <p className="text-sm text-gray-600">{selectedInvoice.notes}</p>
                </div>
              )}
              
              {selectedInvoice.payment_method && (
                <div>
                  <h4 className="font-semibold mb-2">Paiement</h4>
                  <p><strong>Méthode:</strong> {selectedInvoice.payment_method}</p>
                  {selectedInvoice.payment_reference && <p><strong>Référence:</strong> {selectedInvoice.payment_reference}</p>}
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => adminInvoicesAPI.downloadPDF(selectedInvoice.id)}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                📄 Télécharger PDF
              </button>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Création depuis Devis */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowQuoteModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Créer facture depuis devis</h3>
            
            <form onSubmit={handleCreateFromQuote} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Devis</label>
                <select
                  value={quoteFormData.quote_id}
                  onChange={(e) => setQuoteFormData({ ...quoteFormData, quote_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                >
                  <option value="">Sélectionner un devis</option>
                  {quotes.map(quote => (
                    <option key={quote.id} value={quote.id}>
                      {quote.name} - {quote.projectType} ({new Date(quote.created_at).toLocaleDateString('fr-FR')})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Montant TTC (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={quoteFormData.amount}
                  onChange={(e) => setQuoteFormData({ ...quoteFormData, amount: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Taux TVA (%)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={quoteFormData.tva_rate}
                  onChange={(e) => setQuoteFormData({ ...quoteFormData, tva_rate: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowQuoteModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-600 text-black rounded hover:bg-yellow-700"
                >
                  Créer Facture
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Création/Édition Manuelle */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setShowCreateModal(false); setEditingInvoice(null); }}>
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">{editingInvoice ? `Modifier Facture ${editingInvoice.invoice_number || editingInvoice.id.substring(0, 8)}` : 'Nouvelle Facture'}</h3>
            
            <form onSubmit={handleCreateInvoice} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Nom du client *</label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Date d'émission *</label>
                  <input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Date d'échéance</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">Par défaut: +30 jours</p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Date de paiement (si payée)</label>
                  <input
                    type="date"
                    value={formData.paid_date}
                    onChange={(e) => setFormData({ ...formData, paid_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">Laissez vide si non payée</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Adresse complète</label>
                <textarea
                  value={formData.client_address}
                  onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="2"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold">Articles</label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    + Ajouter ligne
                  </button>
                </div>
                
                {formData.items.map((item, idx) => (
                  <div key={idx} className="mb-3 p-3 bg-gray-50 rounded">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold mb-1">Sélectionner un produit ou saisir manuellement</label>
                        <select
                          value={item.product_id}
                          onChange={(e) => handleProductSelect(idx, e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                        >
                          <option value="">-- Choisir un produit --</option>
                          <option value="manual">✏️ Saisie manuelle</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} - {product.price}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-xs font-semibold mb-1">Description *</label>
                        <input
                          type="text"
                          placeholder="Description de l'article"
                          value={item.description}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          required
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-xs font-semibold mb-1">Qté *</label>
                        <input
                          type="number"
                          placeholder="Qté"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          step="0.01"
                          min="0"
                          required
                        />
                      </div>
                      <div className="w-32">
                        <label className="block text-xs font-semibold mb-1">Prix HT *</label>
                        <input
                          type="number"
                          placeholder="Prix HT"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          step="0.01"
                          min="0"
                          required
                        />
                      </div>
                      <div className="w-32">
                        <label className="block text-xs font-semibold mb-1">Total HT</label>
                        <div className="w-full px-2 py-1 bg-gray-100 rounded text-sm flex items-center justify-end font-semibold">
                          {formatPrice(item.total_ht)}
                        </div>
                      </div>
                      {formData.items.length > 1 && (
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="px-2 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Taux TVA (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.tva_rate}
                    onChange={(e) => setFormData({ ...formData, tva_rate: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Remise (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.discount_value}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setFormData({ 
                        ...formData, 
                        discount_value: value,
                        discount_type: value > 0 ? 'percent' : ''
                      });
                    }}
                    className="w-full px-3 py-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">Pourcentage de remise sur le sous-total</p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Total HT calculé</label>
                  <div className="w-full px-3 py-2 bg-gray-100 rounded font-semibold">
                    {(() => {
                      const subtotal = formData.items.reduce((sum, item) => sum + item.total_ht, 0);
                      const discount = formData.discount_value > 0 ? subtotal * (formData.discount_value / 100) : 0;
                      const total = subtotal - discount;
                      return formatPrice(total);
                    })()}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Après remise</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="3"
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingInvoice(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-600 text-black rounded hover:bg-yellow-700"
                >
                  {editingInvoice ? 'Enregistrer' : 'Créer Facture'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Modal Ajouter un Paiement */}
      {showPaymentModal && paymentInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Ajouter un Paiement</h3>
            
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <p className="text-sm"><strong>Facture:</strong> {paymentInvoice.invoice_number}</p>
              <p className="text-sm"><strong>Client:</strong> {paymentInvoice.client_name}</p>
              <p className="text-sm"><strong>Total TTC:</strong> {formatPrice(paymentInvoice.total_ttc)}</p>
              <p className="text-sm"><strong>Déjà payé:</strong> {formatPrice(paymentInvoice.amount_paid || 0)}</p>
              <p className="text-sm font-bold text-orange-600">
                <strong>Restant dû:</strong> {formatPrice(paymentInvoice.amount_due || (paymentInvoice.total_ttc - (paymentInvoice.amount_paid || 0)))}
              </p>
            </div>
            
            <form onSubmit={handleAddPayment} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Montant du paiement (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={paymentInvoice.amount_due || (paymentInvoice.total_ttc - (paymentInvoice.amount_paid || 0))}
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Date du paiement *</label>
                <input
                  type="date"
                  value={paymentData.date}
                  onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Méthode de paiement *</label>
                <select
                  value={paymentData.method}
                  onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                >
                  <option value="manual">Manuel</option>
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Virement bancaire</option>
                  <option value="cash">Espèces</option>
                  <option value="check">Chèque</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Référence du paiement</label>
                <input
                  type="text"
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Ex: Transaction ID, N° de chèque..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Notes</label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="2"
                  placeholder="Notes additionnelles..."
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Ajouter Paiement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
